/* eslint-disable max-len */
import React, { useState, useEffect } from 'react';
import {
    Box,
    TextField,
    Grid,
    Typography,
    Paper,
    Divider,
    RadioGroup,
    Radio,
    FormControlLabel,
    Button,
    Alert,
} from '@mui/material';
import CustomInputTextField from 'components/shared/custom_input_textfield';

export interface Step2SimData {
    referenceYear: number;
    hasFamilyFarmersMarket: boolean | null;
    numberOfFamilyFarmers: number;
    annualMarketValue: number;
    totalFamilyFarmingValue: number;
    numberOfPermanentMarkets: number;
    numberOfNonPermanentMarkets: number;
}

interface Step2Props {
    onNext: () => void;
    onChange: (data: Step2SimData) => void;
    data: Step2SimData;
    disabled?: boolean;
    onBack?: () => void;
}

const Step2: React.FC<Step2Props> = ({
    onNext,
    onChange,
    data,
    disabled = false,
    onBack,
}) => {
    const currentYear = new Date().getFullYear();

    const [referenceYear] = useState<number>(data.referenceYear || currentYear);
    const [hasFamilyFarmersMarket, setHasFamilyFarmersMarket] = useState<boolean | null>(data.hasFamilyFarmersMarket ?? null);
    const [numberOfFamilyFarmers, setNumberOfFamilyFarmers] = useState<number>(data.numberOfFamilyFarmers ?? 0);
    const [annualMarketValue, setAnnualMarketValue] = useState<number>(data.annualMarketValue ?? 0);
    const [totalFamilyFarmingValue, setTotalFamilyFarmingValue] = useState<number>(data.totalFamilyFarmingValue ?? 0);
    const [numberOfPermanentMarkets, setNumberOfPermanentMarkets] = useState<number>(data.numberOfPermanentMarkets ?? 0);
    const [numberOfNonPermanentMarkets, setNumberOfNonPermanentMarkets] = useState<number>(data.numberOfNonPermanentMarkets ?? 0);

    const [errors, setErrors] = useState<{ [key: string]: string }>({});

    useEffect(() => {
        setHasFamilyFarmersMarket(data.hasFamilyFarmersMarket ?? null);
        setNumberOfFamilyFarmers(data.numberOfFamilyFarmers ?? 0);
        setAnnualMarketValue(data.annualMarketValue ?? 0);
        setTotalFamilyFarmingValue(data.totalFamilyFarmingValue ?? 0);
        setNumberOfPermanentMarkets(data.numberOfPermanentMarkets ?? 0);
        setNumberOfNonPermanentMarkets(data.numberOfNonPermanentMarkets ?? 0);
    }, [data]);

    useEffect(() => {
        onChange({
            referenceYear,
            hasFamilyFarmersMarket,
            numberOfFamilyFarmers,
            annualMarketValue,
            totalFamilyFarmingValue,
            numberOfPermanentMarkets,
            numberOfNonPermanentMarkets,
        });
        // eslint-disable-next-line
    }, [
        referenceYear,
        hasFamilyFarmersMarket,
        numberOfFamilyFarmers,
        annualMarketValue,
        totalFamilyFarmingValue,
        numberOfPermanentMarkets,
        numberOfNonPermanentMarkets,
    ]);

    const handleHasMarketChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value === 'sim';
        setHasFamilyFarmersMarket(value);
        if (!value) {
            setNumberOfFamilyFarmers(0);
            setAnnualMarketValue(0);
            setTotalFamilyFarmingValue(0);
            setNumberOfPermanentMarkets(0);
            setNumberOfNonPermanentMarkets(0);
            setErrors({});
        }
    };

    const handleCurrencyChange = (setter: (v: number) => void) => (e: React.ChangeEvent<HTMLInputElement>) => {
        const raw = e.target.value.replace(/\D/g, '');
        setter(Number(raw) / 100);
    };

    const formatCurrency = (value: number) =>
        value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

    const handleSubmit = () => {
        const newErrors: { [key: string]: string } = {};

        if (hasFamilyFarmersMarket) {
            if (!numberOfFamilyFarmers || numberOfFamilyFarmers < 1) newErrors.numberOfFamilyFarmers = 'Informe um número válido.';
            if (!annualMarketValue || annualMarketValue < 1) newErrors.annualMarketValue = 'Informe um valor válido.';
            if (!totalFamilyFarmingValue || totalFamilyFarmingValue < 1) newErrors.totalFamilyFarmingValue = 'Informe um valor válido.';
            if (numberOfPermanentMarkets < 0) newErrors.numberOfPermanentMarkets = 'Informe um número válido.';
            if (numberOfNonPermanentMarkets < 0) newErrors.numberOfNonPermanentMarkets = 'Informe um número válido.';
        }

        setErrors(newErrors);

        if (Object.keys(newErrors).length === 0) {
            onNext();
        }
    };

    return (
        <Box>
            <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <TextField
                            label="Ano de referência do Questionário"
                            type="number"
                            value={referenceYear}
                            onChange={e => {/* Se quiser permitir edição, implemente aqui */ }}
                            inputProps={{ min: 1900, max: 2100 }}
                            fullWidth
                            required
                            disabled
                            sx={{ opacity: 0.5 }}
                            margin="dense"
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography variant="subtitle1" sx={{ mb: 1 }}>
                            O município possui feira da agricultura familiar?
                        </Typography>
                        <RadioGroup
                            row
                            value={hasFamilyFarmersMarket === null ? '' : hasFamilyFarmersMarket ? 'sim' : 'nao'}
                            onChange={handleHasMarketChange}
                            name="has-family-farmers-market"
                        >
                            <FormControlLabel value="sim" control={<Radio />} label="Sim" />
                            <FormControlLabel value="nao" control={<Radio />} label="Não" />
                        </RadioGroup>
                    </Grid>
                </Grid>
            </Paper>

            {hasFamilyFarmersMarket && (
                <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <CustomInputTextField
                                label="Quantas agricultoras e agricultores familiares do município comercializam nas feiras?"
                                type="number"
                                inputProps={{ min: 1 }}
                                value={numberOfFamilyFarmers}
                                onChange={e => setNumberOfFamilyFarmers(Number(e.target.value))}
                                error={errors}
                                helperText={errors}
                                fullWidth
                                margin="dense"
                                required
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                label="Qual o valor comercializado (R$) nas feiras do município anualmente?"
                                value={formatCurrency(annualMarketValue)}
                                onChange={handleCurrencyChange(setAnnualMarketValue)}
                                error={!!errors.annualMarketValue}
                                helperText={errors.annualMarketValue}
                                fullWidth
                                margin="dense"
                                required
                                inputProps={{ inputMode: 'numeric' }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                label="Qual o valor total comercializado pela Agricultura Familiar?"
                                value={formatCurrency(totalFamilyFarmingValue)}
                                onChange={handleCurrencyChange(setTotalFamilyFarmingValue)}
                                error={!!errors.totalFamilyFarmingValue}
                                helperText={errors.totalFamilyFarmingValue}
                                fullWidth
                                margin="dense"
                                required
                                inputProps={{ inputMode: 'numeric' }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <CustomInputTextField
                                label="Quantas feiras permanentes regulares existem no município?"
                                type="number"
                                inputProps={{ min: 0 }}
                                value={numberOfPermanentMarkets}
                                onChange={e => setNumberOfPermanentMarkets(Number(e.target.value))}
                                error={errors}
                                helperText={errors}
                                fullWidth
                                margin="dense"
                                required
                            />
                            <Alert severity="info" sx={{ mt: 1 }}>
                                <strong>Feira permanente:</strong> atividade mercantil de caráter constante realizada em área pública previamente designada pela administração pública regional, com instalações comerciais fixas e edificadas para a comercialização de produtos hortifrutigranjeiros, cereais, doces, laticínios, pescados, animais vivos considerados domésticos, flores, plantas ornamentais, produtos de artesanato, lanches, caldo de cana, temperos, confecções etc.
                            </Alert>
                        </Grid>
                        <Grid item xs={12}>
                            <CustomInputTextField
                                label="Quantas feiras NÃO permanentes regulares existem no município?"
                                type="number"
                                inputProps={{ min: 0 }}
                                value={numberOfNonPermanentMarkets}
                                onChange={e => setNumberOfNonPermanentMarkets(Number(e.target.value))}
                                error={errors}
                                helperText={errors}
                                fullWidth
                                margin="dense"
                                required
                            />
                            <Alert severity="info" sx={{ mt: 1 }}>
                                <strong>Feira não permanente ou feira livre:</strong> atividade mercantil de caráter cíclico, realizada em local público previamente designado pela administração pública regional, com instalações provisórias e removíveis, podendo ocorrer em vias, logradouros públicos ou ainda em área pública coberta do tipo pavilhão.
                            </Alert>
                        </Grid>
                    </Grid>
                </Paper>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
                <Button
                    variant="outlined"
                    onClick={onBack}
                    disabled={disabled}
                >
                    Voltar
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSubmit}
                    size="large"
                    disabled={disabled}
                >
                    Salvar e continuar
                </Button>
            </Box>
        </Box>
    );
};

export default Step2;